import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import useDynamicImports, { LoadingStrategy } from '../../hooks/use-dynamic-imports';

const useLiveDataTransform = <T extends Record<string, unknown>>(
    data: T,
): {
    data: T,
    isPreview: boolean
    hasUpdatedFromCs: boolean
    isFromMicrosite: boolean
} => {
    const { pathname } = useLocation();
    const [isPreview, setIsPreview] = React.useState(false);
    const [currentContentType, setCurrentContentType] = React.useState('');
    const [
        dataPartial,
        setDataPartial,
    ] = React.useState<Partial<T>|null>(null);
    const [
        currentLoadedPathname,
        setCurrentLoadedPathname,
    ] = React.useState<string|null>(null);

    const {
        hasLoaded,
        load,
        scripts: { initCommunication },
    } = useDynamicImports({
        initCommunication: async () => {
            const { default: pkg } = await import('./init-communication');
            return pkg;
        },
    }, LoadingStrategy.Interaction);

    React.useEffect(() => {
        // Step 1: Load in if we're in a window
        if (window.self !== window.top && !hasLoaded) {
            load();
        }
    }, [hasLoaded, load]);

    React.useEffect(() => {
        // Step 2: Initialize the events manager
        if (hasLoaded && currentLoadedPathname !== pathname) {
            initCommunication(
                setDataPartial,
                setCurrentContentType,
            );
            setIsPreview(true);
            setCurrentLoadedPathname(pathname);
        }
    }, [currentLoadedPathname, data, hasLoaded, initCommunication, pathname]);

    const isFromMicrosite = currentContentType === 'csFranchisePageBuilder'
      || currentContentType === 'csFranchiseServicePages';

    if (dataPartial && data !== undefined && currentLoadedPathname === pathname) {
        return {
            data: {
                ...data,
                ...dataPartial,
            },
            isPreview,
            hasUpdatedFromCs: true,
            isFromMicrosite,
        };
    }

    return {
        data,
        isPreview,
        hasUpdatedFromCs: false,
        isFromMicrosite,
    };
};

export default useLiveDataTransform;
